import React, { useState, useEffect, useCallback } from 'react';
import { http } from '../../lib/http';
import PaymentHistoryTable from '../shared/PaymentHistoryTable';
import { getStartDateForRange } from '../../lib/time';

const DATE_RANGE_OPTIONS = [
  { label: 'All Time', value: 'all' },
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'Last 6 Months', value: '6months' },
  { label: 'This Year', value: 'year' },
];

const PaymentHistoryTab = ({ setMessage, setError }) => {
    const [incomeTransactions, setIncomeTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [dateRange, setDateRange] = useState('all');
    const [exportingCsv, setExportingCsv] = useState(false);

    const buildDateParams = useCallback(() => {
        if (dateRange === 'all') return '';
        const startDate = getStartDateForRange(dateRange);
        return startDate ? `&startDate=${startDate}` : '';
    }, [dateRange]);

    const loadIncomeTransactions = useCallback(async (pageNum = 1, isInitial = false) => {
        try {
            if (isInitial) setLoading(true);
            else setLoadingMore(true);

            const dateParams = buildDateParams();
            const response = await http.get(
                `/owner/income/all?page=${pageNum}&limit=1000${dateParams}`
            );

            if (response.data.success) {
                const newTransactions = response.data.data?.transactions || [];
                if (isInitial) {
                    setIncomeTransactions(newTransactions);
                } else {
                    setIncomeTransactions(prev => [...prev, ...newTransactions]);
                }
                setPage(pageNum);
                setHasMore(pageNum < (response.data.data?.totalPages || 1));
            }
        } catch (error) {
            console.error('Failed to load income transactions:', error);
            setError?.('Failed to load payment history');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [buildDateParams, setError]);

    useEffect(() => {
        loadIncomeTransactions(1, true);
    }, [loadIncomeTransactions]);

    const handleLoadMore = () => {
        if (!loadingMore && hasMore) {
            loadIncomeTransactions(page + 1);
        }
    };

    const handleExportCsv = async () => {
        setExportingCsv(true);
        try {
            const headers = ['Date', 'Customer', 'Email', 'Details', 'Method', 'Amount'];
            const rows = incomeTransactions.map(t => [
                t.payment_date
                    ? new Date(t.payment_date).toLocaleString('en-CA', {
                          timeZone: 'America/Toronto',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                      })
                    : '',
                t.customer_name || '',
                t.customer_email || '',
                t.plan_key || t.description || '',
                t.payment_method || 'Manual',
                parseFloat(t.total_amount || 0).toFixed(2),
            ]);

            const csvContent = [headers, ...rows]
                .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
                .join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            const date = new Date().toISOString().split('T')[0];
            link.href = url;
            link.download = `payments_${date}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            setError?.('Failed to export CSV');
        } finally {
            setExportingCsv(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mb-4"></div>
                    <p className="text-gray-600">Loading payment history...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Payment History</h2>

                    <div className="flex flex-wrap items-center gap-2">
                        {/* Date range filter */}
                        <select
                            value={dateRange}
                            onChange={e => setDateRange(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-orange-500 focus:border-orange-500 bg-white"
                        >
                            {DATE_RANGE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>

                        {/* CSV Export */}
                        <button
                            onClick={handleExportCsv}
                            disabled={exportingCsv || incomeTransactions.length === 0}
                            className="px-4 py-2 bg-gray-700 hover:bg-gray-800 disabled:opacity-50 text-white rounded-md font-medium text-sm transition-colors"
                        >
                            {exportingCsv ? 'Exporting...' : 'Export CSV'}
                        </button>

                        {/* Refresh */}
                        <button
                            onClick={() => loadIncomeTransactions(1, true)}
                            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-medium text-sm transition-colors"
                        >
                            Refresh
                        </button>
                    </div>
                </div>

                <PaymentHistoryTable
                    transactions={incomeTransactions}
                    onDelete={null}
                    userRole="owner"
                />

                {hasMore && (
                    <div className="mt-8 flex justify-center">
                        <button
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                            className={`px-8 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-md font-bold shadow-lg transition-all border-2 border-orange-500 flex items-center gap-2 ${loadingMore ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {loadingMore && (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            )}
                            {loadingMore ? 'Loading...' : 'Load More Transactions'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentHistoryTab;
